import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import {
  formsApi,
  type FormEntity,
  type FieldType,
  type Field,
  type Section,
  type FieldInput,
} from '../features/forms/api.ts';
import { dv } from '../i18n/dv.ts';

const ENTITIES: FormEntity[] = ['RESIDENT', 'REGISTERED_BUSINESS', 'HOME_BUSINESS'];
const TYPES: FieldType[] = ['TEXT', 'NUMBER', 'SELECT', 'CHOICE', 'MULTISELECT', 'DATE'];
const NEEDS_OPTIONS = (t: FieldType): boolean => t === 'SELECT' || t === 'CHOICE' || t === 'MULTISELECT';

interface DraftState {
  label: string;
  type: FieldType;
  required: boolean;
  options: string[];
}

function emptyDraft(): DraftState {
  return { label: '', type: 'TEXT', required: false, options: [] };
}

function fieldToDraft(f: Field): DraftState {
  return { label: f.label, type: f.type, required: f.required, options: f.options ?? [] };
}

/** Add / edit a field within a section. */
function FieldForm({
  entity, sectionId, editing, onDone, onCancel,
}: {
  entity: FormEntity;
  sectionId: string;
  editing: Field | null;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftState>(() => (editing ? fieldToDraft(editing) : emptyDraft()));
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['form-fields', entity] });

  const save = useMutation({
    mutationFn: () => {
      const options = NEEDS_OPTIONS(draft.type)
        ? draft.options.map((o) => o.trim()).filter(Boolean)
        : undefined;
      if (editing) {
        return formsApi.updateField(editing.id, {
          label: draft.label.trim(),
          required: draft.required,
          options: NEEDS_OPTIONS(draft.type) ? options ?? [] : null,
        });
      }
      const input: FieldInput = {
        type: draft.type,
        label: draft.label.trim(),
        required: draft.required,
        ...(options ? { options } : {}),
      };
      return formsApi.createField(entity, sectionId, input);
    },
    onSuccess: () => { void invalidate(); onDone(); },
    onError: () => setError(dv.common.error),
  });

  const cleanOptions = draft.options.map((o) => o.trim()).filter(Boolean);
  const optionsInvalid = NEEDS_OPTIONS(draft.type) && cleanOptions.length === 0;

  const setOption = (i: number, val: string): void =>
    setDraft((d) => ({ ...d, options: d.options.map((o, idx) => (idx === i ? val : o)) }));
  const addOption = (): void => setDraft((d) => ({ ...d, options: [...d.options, ''] }));
  const removeOption = (i: number): void =>
    setDraft((d) => ({ ...d, options: d.options.filter((_, idx) => idx !== i) }));

  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!draft.label.trim()) return;
        if (optionsInvalid) { setError(dv.forms.optionsRequired); return; }
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <label className="field-label">{dv.forms.label}</label>
        <input
          required
          className="input"
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="field-label">{dv.forms.type}</label>
          <select
            className="input"
            value={draft.type}
            disabled={editing !== null}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value as FieldType }))}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>{dv.forms.typeLabel[t]}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pt-6 text-sm">
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
          />
          <span>{dv.forms.required}</span>
        </label>
      </div>

      {NEEDS_OPTIONS(draft.type) && (
        <div className="space-y-2">
          <label className="field-label">{dv.forms.options}</label>
          {draft.options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder={dv.forms.optionPlaceholder}
                value={opt}
                onChange={(e) => setOption(i, e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeOption(i)}
                className="btn-ghost px-2 py-1 text-xs shrink-0"
                aria-label={dv.common.remove}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addOption} className="btn-ghost px-2 py-1 text-xs">
            <Icon name="add" size={14} /> {dv.forms.addOption}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={save.isPending || !draft.label.trim() || optionsInvalid}
          className="btn-primary"
        >
          {save.isPending ? dv.common.saving : dv.common.save}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">{dv.common.cancel}</button>
      </div>
    </form>
  );
}

/** A single field row inside a section. */
function FieldRow({
  entity, section, field, index, canUp, canDown,
}: {
  entity: FormEntity;
  section: Section;
  field: Field;
  index: number;
  canUp: boolean;
  canDown: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['form-fields', entity] });

  const remove = useMutation({
    mutationFn: () => formsApi.remove(field.id),
    onSuccess: () => { void invalidate(); },
  });

  const reorder = useMutation({
    mutationFn: (dir: -1 | 1) => {
      const ids = section.fields.map((f) => f.id);
      const target = index + dir;
      const a = ids[index];
      const b = ids[target];
      if (a === undefined || b === undefined) return Promise.resolve();
      ids[index] = b;
      ids[target] = a;
      return formsApi.reorderFields(entity, section.id, ids);
    },
    onSuccess: () => { void invalidate(); },
  });

  if (editing) {
    return (
      <li className="p-3">
        <FieldForm
          entity={entity}
          sectionId={section.id}
          editing={field}
          onDone={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-2 px-3 py-2">
      <div className="min-w-0">
        <p className="font-medium leading-snug truncate">
          {field.label}
          {field.required && (
            <span className="pill pill-active ms-2">
              {dv.forms.requiredMark}
            </span>
          )}
        </p>
        <p className="mono-meta text-muted-foreground truncate">
          {dv.forms.typeLabel[field.type]}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => reorder.mutate(-1)}
          disabled={!canUp || reorder.isPending}
          className="btn-ghost px-2 py-1"
          aria-label={dv.forms.moveUp}
        >
          <Icon name="expand_less" size={15} />
        </button>
        <button
          type="button"
          onClick={() => reorder.mutate(1)}
          disabled={!canDown || reorder.isPending}
          className="btn-ghost px-2 py-1"
          aria-label={dv.forms.moveDown}
        >
          <Icon name="expand_more" size={15} />
        </button>
        {field.system ? (
          // The seeded Full name field is locked: no edit/type change, no delete.
          <span className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--surface-container-highest))] px-2 py-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
            <Icon name="lock" size={12} /> {dv.forms.systemDefault}
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-ghost px-2 py-1"
              aria-label={dv.forms.editField}
            >
              <Icon name="edit" size={15} />
            </button>
            <button
              type="button"
              onClick={() => { if (window.confirm(dv.forms.deleteFieldConfirm)) remove.mutate(); }}
              disabled={remove.isPending}
              className="btn-ghost px-2 py-1 text-destructive"
              aria-label={dv.forms.deleteField}
            >
              <Icon name="delete" size={15} />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/** A section card: rename / delete / reorder the section, plus its fields. */
function SectionCard({
  entity, section, canUp, canDown, onMove, moving,
}: {
  entity: FormEntity;
  section: Section;
  canUp: boolean;
  canDown: boolean;
  onMove: (dir: -1 | 1) => void;
  moving: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(section.label);
  const [addingField, setAddingField] = useState(false);
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['form-fields', entity] });

  const rename = useMutation({
    mutationFn: () => formsApi.updateField(section.id, { label: name.trim() }),
    onSuccess: () => { void invalidate(); setRenaming(false); },
  });

  const remove = useMutation({
    mutationFn: () => formsApi.remove(section.id),
    onSuccess: () => { void invalidate(); },
  });

  return (
    <div className="card-paper overflow-hidden">
      <header className="flex items-center justify-between gap-2 border-b border-border bg-muted/20 px-3 py-2">
        {renaming ? (
          <form
            className="flex flex-1 items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); if (name.trim()) rename.mutate(); }}
          >
            <input
              className="input flex-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label={dv.forms.sectionName}
              autoFocus
            />
            <button type="submit" disabled={rename.isPending || !name.trim()} className="btn-ghost px-2 py-1" aria-label={dv.common.save}>
              <Icon name="check" size={15} />
            </button>
            <button type="button" onClick={() => { setRenaming(false); setName(section.label); }} className="btn-ghost px-2 py-1" aria-label={dv.common.cancel}>
              <Icon name="close" size={15} />
            </button>
          </form>
        ) : (
          <>
            <h3 className="font-serif text-base font-bold leading-tight truncate">{section.label}</h3>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onMove(-1)}
                disabled={!canUp || moving}
                className="btn-ghost px-2 py-1"
                aria-label={dv.forms.moveUp}
              >
                <Icon name="expand_less" size={15} />
              </button>
              <button
                type="button"
                onClick={() => onMove(1)}
                disabled={!canDown || moving}
                className="btn-ghost px-2 py-1"
                aria-label={dv.forms.moveDown}
              >
                <Icon name="expand_more" size={15} />
              </button>
              {section.system ? (
                // The seeded Basic Information section is locked: no rename, no delete.
                <span className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--surface-container-highest))] px-2 py-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
                  <Icon name="lock" size={12} /> {dv.forms.systemDefault}
                </span>
              ) : (
                <>
                  <button type="button" onClick={() => { setName(section.label); setRenaming(true); }} className="btn-ghost px-2 py-1" aria-label={dv.forms.renameSection}>
                    <Icon name="edit" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => { if (window.confirm(dv.forms.deleteSectionConfirm)) remove.mutate(); }}
                    disabled={remove.isPending}
                    className="btn-ghost px-2 py-1 text-destructive"
                    aria-label={dv.forms.deleteSection}
                  >
                    <Icon name="delete" size={15} />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </header>

      {section.fields.length > 0 && (
        <ul className="divide-y divide-border">
          {section.fields.map((field, i) => (
            <FieldRow
              key={field.id}
              entity={entity}
              section={section}
              field={field}
              index={i}
              canUp={i > 0}
              canDown={i < section.fields.length - 1}
            />
          ))}
        </ul>
      )}

      <div className="p-3">
        {addingField ? (
          <FieldForm
            entity={entity}
            sectionId={section.id}
            editing={null}
            onDone={() => setAddingField(false)}
            onCancel={() => setAddingField(false)}
          />
        ) : (
          <button type="button" onClick={() => setAddingField(true)} className="btn-secondary">
            <Icon name="add" size={15} /> {dv.forms.addField}
          </button>
        )}
      </div>
    </div>
  );
}

function EntityForms({ entity }: { entity: FormEntity }): JSX.Element {
  const qc = useQueryClient();
  const { data: sections, isLoading } = useQuery({
    queryKey: ['form-fields', entity],
    queryFn: () => formsApi.list(entity),
  });
  const [addingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['form-fields', entity] });

  const list = sections ?? [];

  const addSection = useMutation({
    mutationFn: () => formsApi.createSection(entity, sectionName.trim()),
    onSuccess: () => { setSectionName(''); setAddingSection(false); setError(null); void invalidate(); },
    onError: () => setError(dv.common.error),
  });

  const reorderSections = useMutation({
    mutationFn: (orderedIds: string[]) => formsApi.reorderSections(entity, orderedIds),
    onSuccess: () => { void invalidate(); },
  });

  const moveSection = (index: number, dir: -1 | 1): void => {
    const ids = list.map((s) => s.id);
    const target = index + dir;
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    reorderSections.mutate(ids);
  };

  return (
    <div className="space-y-3">
      {list.length === 0 && !addingSection ? (
        <p className="text-sm text-muted-foreground">
          {isLoading ? dv.common.loading : dv.forms.noSections}
        </p>
      ) : (
        <div className="space-y-3">
          {list.map((section, i) => (
            <SectionCard
              key={section.id}
              entity={entity}
              section={section}
              canUp={i > 0}
              canDown={i < list.length - 1}
              onMove={(dir) => moveSection(i, dir)}
              moving={reorderSections.isPending}
            />
          ))}
        </div>
      )}

      {addingSection ? (
        <form
          className="space-y-2 rounded-lg border border-border bg-muted/20 p-3"
          onSubmit={(e) => { e.preventDefault(); if (sectionName.trim()) addSection.mutate(); }}
        >
          <div className="space-y-1">
            <label className="field-label">{dv.forms.sectionName}</label>
            <input
              required
              className="input"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={addSection.isPending || !sectionName.trim()} className="btn-primary">
              {addSection.isPending ? dv.common.saving : dv.common.save}
            </button>
            <button type="button" onClick={() => { setAddingSection(false); setSectionName(''); setError(null); }} className="btn-ghost">
              {dv.common.cancel}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={() => setAddingSection(true)} className="btn-secondary">
          <Icon name="add" size={15} /> {dv.forms.addSection}
        </button>
      )}
    </div>
  );
}

export function FormsBuilder(): JSX.Element {
  const [entity, setEntity] = useState<FormEntity>('RESIDENT');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{dv.forms.intro}</p>

      <div className="flex flex-wrap gap-2">
        {ENTITIES.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setEntity(e)}
            aria-pressed={entity === e}
            className={entity === e ? 'btn-primary' : 'btn-secondary'}
          >
            {dv.forms.entityLabel[e]}
          </button>
        ))}
      </div>

      <section className="card-paper p-4">
        <EntityForms entity={entity} />
      </section>
    </div>
  );
}
